import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Layers, Loader2, AlertCircle, Check, RefreshCw } from "lucide-react";
import { getKitTiers, updateKitTier, KitTier } from "../../api/services/adminKitTierService";
import { getErrorMessage } from "@/utils/errorUtils";

const fld = "w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500";

export default function AdminKitTiers() {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState<KitTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const t = await getKitTiers();
      t.sort((a, b) => (a.order || 0) - (b.order || 0));
      setTiers(t);
    } catch (e: unknown) {
      setErr(getErrorMessage(e) || "Failed to load kit tiers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setField = (key: string, field: keyof KitTier, value: string | number | boolean) => {
    setTiers((prev) => prev.map((t) => (t.key === key ? { ...t, [field]: value } : t)));
  };

  const save = async (tier: KitTier) => {
    if (tier.price === undefined || tier.price === null || (tier.price as string | number) === "" || Number(tier.price) < 0) {
      setErr(`${tier.label}: price must be a non-negative number`); return;
    }
    setSavingKey(tier.key); setErr(""); setSavedKey(null);
    try {
      const updated = await updateKitTier(tier.key, {
        label: tier.label,
        description: tier.description,
        price: Number(tier.price),
        currency: tier.currency,
        enabled: tier.enabled,
      });
      if (updated) {
        setTiers((prev) => prev.map((t) => (t.key === tier.key ? { ...t, ...updated } : t)));
        setSavedKey(tier.key);
        setTimeout(() => setSavedKey(null), 2500);
      }
    } catch (e: unknown) {
      setErr(getErrorMessage(e) || "Failed to save tier");
    } finally { setSavingKey(null); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Layers className="w-6 h-6 text-blue-600" />
          <h1 className="text-xl sm:text-2xl font-bold">{t('admin.kittiers.title', 'Kit Tiers &amp; Fees')}</h1>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-800">
          <RefreshCw className="w-4 h-4" /> {t('admin.kittiers.button.refresh', 'Refresh')}
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        {t('admin.kittiers.description1', 'These are the one-time activation fees operators pay when registering a BYO (bring-your-own) kit.')}
        <br/>
        {t('admin.kittiers.description2', 'Changes apply immediately to new kit registrations. Disabling a tier hides it from the registration flow.')}
      </p>

      {err && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 px-4 py-3 text-sm">
          <AlertCircle className="w-4 h-4" /> {err}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>
      ) : tiers.length === 0 ? (
        <div className="text-center py-16 text-gray-500">{t('admin.kittiers.noTiers', 'No tiers found.')}</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          {tiers.map((tier) => (
            <Card key={tier.key} className="p-5 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono uppercase tracking-wide text-gray-400">{tier.key}</span>
                <label className="flex items-center gap-1.5 text-xs">
                  <input type="checkbox" checked={!!tier.enabled} onChange={(e) => setField(tier.key, "enabled", e.target.checked)} />
                  {tier.enabled ? t('admin.kittiers.enabled', 'Enabled') : t('admin.kittiers.disabled', 'Disabled')}
                </label>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.kittiers.form.label', 'Label')}</label>
                <input className={fld} value={tier.label || ""} onChange={(e) => setField(tier.key, "label", e.target.value)} />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.kittiers.form.fee', 'Fee (price)')}</label>
                  <input type="number" min={0} className={fld} value={tier.price ?? 0} onChange={(e) => setField(tier.key, "price", e.target.value)} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.kittiers.form.currency', 'Currency')}</label>
                  <input className={fld} value={tier.currency || "USD"} onChange={(e) => setField(tier.key, "currency", e.target.value)} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">{t('admin.kittiers.form.description', 'Description')}</label>
                <textarea className={fld} rows={2} value={tier.description || ""} onChange={(e) => setField(tier.key, "description", e.target.value)} />
              </div>

              <button
                onClick={() => save(tier)}
                disabled={savingKey === tier.key}
                className="mt-1 inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {savingKey === tier.key ? <Loader2 className="w-4 h-4 animate-spin" /> : savedKey === tier.key ? <Check className="w-4 h-4" /> : null}
                {savedKey === tier.key ? t('admin.kittiers.button.saved', 'Saved') : t('admin.kittiers.button.save', 'Save')}
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
